import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Smartphone, X, Share, Plus } from "lucide-react";

const STORAGE_KEY = "frenteya_install_dismissed";

export const InstallAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY) === "1") setDismissed(true);

    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/.test(ua);
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsIos(ios);
    if (standalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    } else if (isIos) {
      setShowIosHelp(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDismissed(true);
  };

  // Mostrar si: hay prompt nativo, o es iOS (que necesita instrucciones manuales)
  const visible = !installed && !dismissed && (deferredPrompt || isIos);
  if (!visible) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, delay: 1.5 }}
        className="fixed bottom-24 left-5 z-40 max-w-[280px] bg-white border-2 border-[#0A192F] shadow-2xl"
        data-testid="install-banner"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-[#9CA3AF] hover:text-[#0A192F]"
          aria-label="Cerrar"
          data-testid="install-dismiss"
        >
          <X size={14} />
        </button>
        <div className="p-4 pr-7">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-8 h-8 bg-[#0A192F] flex items-center justify-center">
              <Smartphone className="text-[#F5B400]" size={16} />
            </div>
            <span className="font-headings text-[10px] uppercase tracking-[0.25em] text-[#0A2463]">
              Instalá la app
            </span>
          </div>
          <p className="text-xs text-[#4B5563] leading-snug mb-3">
            Llevá <strong className="text-[#0A192F]">Frente Ya</strong> en tu pantalla de inicio.
          </p>
          <button
            onClick={handleInstall}
            className="w-full bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide text-xs py-2.5 inline-flex items-center justify-center gap-1.5"
            data-testid="install-cta"
          >
            <Download size={14} /> Instalar
          </button>
        </div>
      </motion.div>

      {/* Modal de ayuda para iOS (que no expone beforeinstallprompt) */}
      <AnimatePresence>
        {showIosHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowIosHelp(false)}
            data-testid="install-ios-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9 }}
              className="bg-white max-w-sm w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowIosHelp(false)}
                className="absolute top-3 right-3 text-[#9CA3AF] hover:text-[#0A192F]"
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
              <h3 className="font-headings text-xl uppercase font-bold text-[#0A192F] mb-4">
                Instalar en iPhone
              </h3>
              <ol className="space-y-3 text-sm text-[#4B5563]">
                <li className="flex items-start gap-3">
                  <span className="font-headings text-[#F5B400] font-bold">1.</span>
                  <span>Tocá el botón <Share className="inline mx-1 align-middle text-[#0A2463]" size={16} /> <strong>Compartir</strong> en la barra de Safari.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-headings text-[#F5B400] font-bold">2.</span>
                  <span>Bajá y elegí <strong>"Añadir a pantalla de inicio"</strong> <Plus className="inline mx-1 align-middle text-[#0A2463]" size={16} />.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-headings text-[#F5B400] font-bold">3.</span>
                  <span>Tocá <strong>"Añadir"</strong> arriba a la derecha.</span>
                </li>
              </ol>
              <button
                onClick={() => setShowIosHelp(false)}
                className="mt-6 w-full bg-[#0A192F] text-white font-headings uppercase tracking-wide text-sm py-3"
              >
                Entendido
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InstallAppButton;
